import React, { useEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";
import SegmentComparision from "./SegmentComparision";
import TempDropDown from "../../../../components/Graphs/PersonaPredictionGraphs/TempDropDown";

const OverallStatistics = () => {
  // State for the Segment Size Distribution dropdown
  const [selectedSegment, setSelectedSegment] = useState("device");
  const [currentWeek, setCurrentWeek] = useState("2025-03-24");
  const [weekLabels, setWeekLabels] = useState([
    // "2025-03-10",
    // "2025-03-17",
    "2025-03-24",
  ]);

  // Mapping for different segment types (for Segment Size Distribution)
  const segmentDataSets = {
    device: {
      labels: ["Mobile", "Desktop"],
      data: [1504, 579, 10],
      backgroundColor: [
        "rgba(16, 185, 129, 0.8)",
        "rgba(245, 158, 11, 0.8)",
      ],
    },
    userType: {
      labels: ["New Visitors", "Returning Visitors"],
      data: [1201, 882],
      backgroundColor: ["rgba(59, 130, 246, 0.8)", "rgba(139, 92, 246, 0.8)"],
    },
    trafficSource: {
      labels: ["Organic", "Paid", "Referral", "Social"],
      data: [1195, 888, 15, 15],
      backgroundColor: [
        "rgba(16, 185, 129, 0.8)",
        "rgba(59, 130, 246, 0.8)",
        "rgba(245, 158, 11, 0.8)",
        "rgba(239, 68, 68, 0.8)",
      ],
    },
  };

  // State for Engagement metric selection
  const [engagementMetric, setEngagementMetric] = useState("Pages per Session");

  // Mapping for different engagement metrics (for Engagement by Segment)
  const engagementMetricsData = {
    "Pages per Session": [2.4, 4.7, 3.1, 5.2, 4.3, 2.8],
    "Session Duration": [3.5, 5.0, 4.2, 6.0, 5.0, 3.2],
    "Bounce Rate": [40, 50, 45, 55, 60, 50],
  };

  // Refs for Chart.js canvases
  const segmentSizeChartRef = useRef(null);
  const engagementChartRef = useRef(null);
  const conversionFunnelChartRef = useRef(null);
  const temporalChartRef = useRef(null);
  const comparisonChartRef = useRef(null);

  // useEffect for the Segment Size Doughnut Chart (depends on selectedSegment)
  useEffect(() => {
    let segmentSizeChartInstance;
    if (segmentSizeChartRef.current) {
      const { labels, data, backgroundColor } =
        segmentDataSets[selectedSegment];
      segmentSizeChartInstance = new Chart(segmentSizeChartRef.current, {
        type: "doughnut",
        data: {
          labels,
          datasets: [
            {
              data,
              backgroundColor,
              borderWidth: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                padding: 20,
                boxWidth: 12,
              },
            },
          },
          cutout: "70%",
        },
      });
    }
    return () => {
      segmentSizeChartInstance?.destroy();
    };
  }, [selectedSegment]);

  // useEffect for the Engagement Bar Chart (initialize once)
  useEffect(() => {
    let engagementChartInstance;
    if (engagementChartRef.current) {
      engagementChartInstance = new Chart(engagementChartRef.current, {
        type: "bar",
        data: {
          labels: [
            "New Visitors",
            "Returning",
            "Mobile",
            "Desktop",
            "Organic",
            "Paid",
          ],
          datasets: [
            {
              label: engagementMetric,
              data: engagementMetricsData[engagementMetric],
              backgroundColor: "rgba(59, 130, 246, 0.8)",
              borderWidth: 0,
              borderRadius: 4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            y: {
              beginAtZero: true,
              grid: { display: true, color: "rgba(0, 0, 0, 0.05)" },
            },
            x: { grid: { display: false } },
          },
        },
      });
    }
    return () => {
      engagementChartInstance?.destroy();
    };
  }, []); // run once on mount

  // Update engagement chart data when engagementMetric changes
  useEffect(() => {
    if (engagementChartRef.current) {
      const chart = Chart.getChart(engagementChartRef.current);
      if (chart) {
        chart.data.datasets[0].label = engagementMetric;
        chart.data.datasets[0].data = engagementMetricsData[engagementMetric];
        chart.update();
      }
    }
  }, [engagementMetric]);

  // useEffect for the other charts (run once on mount)
  useEffect(() => {
    let conversionFunnelChartInstance,
      temporalChartInstance,
      comparisonChartInstance;

    // Conversion Funnel (Horizontal Bar Chart)
    if (conversionFunnelChartRef.current) {
      conversionFunnelChartInstance = new Chart(
        conversionFunnelChartRef.current,
        {
          type: "bar",
          data: {
            labels: [
              "Visit",
              "Product View",
              "Add to Cart",
              "Checkout",
              "Purchase",
            ],
            datasets: [
              {
                label: "Conversion",
                data: [100, 68, 42, 35, 28],
                backgroundColor: [
                  "rgba(59, 130, 246, 0.8)",
                  "rgba(96, 165, 250, 0.8)",
                  "rgba(147, 197, 253, 0.8)",
                  "rgba(191, 219, 254, 0.8)",
                  "rgba(219, 234, 254, 0.8)",
                ],
                borderWidth: 0,
              },
            ],
          },
          options: {
            indexAxis: "y",
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (context) => `${context.label}: ${context.parsed.x}%`,
                },
              },
            },
            scales: {
              x: { beginAtZero: true, max: 100 },
              y: { grid: { display: false } },
            },
          },
        }
      );
    }

    // Temporal Analysis (Line Chart with Fill)
    if (temporalChartRef.current) {
      temporalChartInstance = new Chart(temporalChartRef.current, {
        type: "line",
        data: {
          labels: [
            "12am",
            "1am",
            "2am",
            "3am",
            "4am",
            "5am",
            "6am",
            "7am",
            "8am",
            "9am",
            "10am",
            "11am",
            "12pm",
            "1pm",
            "2pm",
            "3pm",
            "4pm",
            "5pm",
            "6pm",
            "7pm",
            "8pm",
            "9pm",
            "10pm",
            "11pm",
          ],
          datasets: [
            {
              label: "Mobile Users",
              data: [
                30, 25, 15, 10, 15, 30, 45, 60, 75, 70, 60, 80, 95, 90, 85, 80,
                85, 90, 95, 100, 90, 80, 70, 50,
              ],
              fill: true,
              borderColor: "#3b82f6",
              backgroundColor: "rgba(59, 130, 246, 0.2)",
              tension: 0.4,
            },
            {
              label: "Desktop Users",
              data: [
                20, 15, 10, 5, 10, 20, 35, 55, 75, 90, 100, 90, 80, 75, 70, 65,
                60, 70, 80, 85, 75, 65, 50, 35,
              ],
              fill: true,
              borderColor: "#8b5cf6",
              backgroundColor: "rgba(139, 92, 246, 0.2)",
              tension: 0.4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: "top" } },
          scales: { y: { beginAtZero: true } },
        },
      });
    }

    // Comparison Radar Chart
    if (comparisonChartRef.current) {
      comparisonChartInstance = new Chart(comparisonChartRef.current, {
        type: "radar",
        data: {
          labels: [
            "Engagement",
            "Conversion",
            "Retention",
            "Page Views",
            "Session Duration",
          ],
          datasets: [
            {
              label: "Mobile",
              data: [80, 50, 65, 90, 70],
              backgroundColor: "rgba(59, 130, 246, 0.2)",
              borderColor: "#3b82f6",
              pointBackgroundColor: "#3b82f6",
            },
            {
              label: "Desktop",
              data: [60, 90, 75, 60, 85],
              backgroundColor: "rgba(139, 92, 246, 0.2)",
              borderColor: "#8b5cf6",
              pointBackgroundColor: "#8b5cf6",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: { r: { beginAtZero: true } },
          plugins: { legend: { position: "top" } },
        },
      });
    }

    return () => {
      conversionFunnelChartInstance?.destroy();
      temporalChartInstance?.destroy();
      comparisonChartInstance?.destroy();
    };
  }, []);

  return (
    <div className="bg-gray-50 min-h-screen mt-6">
      <div className="container mx-auto px-4 py-8">
        {/* Date Range Selector */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-4">
            <div className="text-sm font-medium text-gray-700">Segment:</div>
            <select className="border border-gray-300 rounded-md px-3 py-2 bg-white text-sm">
              <option>All Segments</option>
              <option>New Visitors</option>
              <option>Returning Visitors</option>
              <option>Mobile Users</option>
              <option>Desktop Users</option>
              <option>Organic Traffic</option>
              <option>Paid Traffic</option>
            </select>
          </div>
          <div className="flex items-center space-x-4">
            <div className="border-[1.25px] w-fit rounded-md border-gray-200">
              {/* <TempDropDown */}
              <TempDropDown
                currentWeek={currentWeek}
                setCurrentWeek={setCurrentWeek}
                weeks={weekLabels}
              />
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-md p-4 transition-all duration-300 hover:shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-blue-600">2,085</div>
                <div className="text-sm text-gray-500">Total Users</div>
              </div>
              <div className="text-green-500 text-sm font-medium">+12.4%</div>
            </div>
            <div className="mt-2 h-1 bg-gray-200 rounded-full">
              <div
                className="h-1 bg-blue-600 rounded-full"
                style={{ width: "75%" }}
              ></div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 transition-all duration-300 hover:shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-purple-600">3.8</div>
                <div className="text-sm text-gray-500">Pages / Session</div>
              </div>
              <div className="text-red-500 text-sm font-medium">-2.1%</div>
            </div>
            <div className="mt-2 h-1 bg-gray-200 rounded-full">
              <div
                className="h-1 bg-purple-600 rounded-full"
                style={{ width: "65%" }}
              ></div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 transition-all duration-300 hover:shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-green-600">4:32</div>
                <div className="text-sm text-gray-500">
                  Avg. Session Duration
                </div>
              </div>
              <div className="text-green-500 text-sm font-medium">+8.7%</div>
            </div>
            <div className="mt-2 h-1 bg-gray-200 rounded-full">
              <div
                className="h-1 bg-green-600 rounded-full"
                style={{ width: "82%" }}
              ></div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 transition-all duration-300 hover:shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-orange-600">28.3%</div>
                <div className="text-sm text-gray-500">Conversion Rate</div>
              </div>
              <div className="text-green-500 text-sm font-medium">+5.2%</div>
            </div>
            <div className="mt-2 h-1 bg-gray-200 rounded-full">
              <div
                className="h-1 bg-orange-600 rounded-full"
                style={{ width: "28%" }}
              ></div>
            </div>
          </div>
        </div>

        {/* Segment Size and Engagement */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Segment Size Distribution */}
          <div className="bg-white rounded-lg shadow-md p-4 transition-all duration-300 hover:shadow-lg">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Segment Size Distribution
            </h2>
            {/* Dropdown to select segment type */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Segment Type:
              </label>
              <select
                value={selectedSegment}
                onChange={(e) => setSelectedSegment(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 bg-white text-sm"
              >
                <option value="device">Device Type</option>
                <option value="userType">User Type</option>
                <option value="trafficSource">Traffic Source</option>
              </select>
            </div>
            <div>
              <canvas ref={segmentSizeChartRef} height="250"></canvas>
            </div>
          </div>
          {/* Engagement by Segment */}
          <div className="bg-white rounded-lg shadow-md p-4 transition-all duration-300 hover:shadow-lg">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Engagement by Segment
            </h2>
            <div className="flex space-x-2 mb-4">
              <select
                value={engagementMetric}
                onChange={(e) => setEngagementMetric(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1 bg-white text-sm"
              >
                <option>Pages per Session</option>
                <option>Session Duration</option>
                <option>Bounce Rate</option>
              </select>
            </div>
            <div>
              <canvas ref={engagementChartRef} height="250"></canvas>
            </div>
          </div>
        </div>

        {/* Conversion Funnel and Temporal Analysis */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Conversion Funnel */}
          <div className="bg-white rounded-lg shadow-md p-4 transition-all duration-300 hover:shadow-lg">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Conversion Funnel
            </h2>
            <div>
              <canvas ref={conversionFunnelChartRef} height="250"></canvas>
            </div>
          </div>
          {/* User Activity by Time of Day */}
          <div className="bg-white rounded-lg shadow-md p-4 transition-all duration-300 hover:shadow-lg">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              User Activity by Time of Day
            </h2>
            <div>
              <canvas ref={temporalChartRef} height="250"></canvas>
            </div>
          </div>
        </div>

        {/* Content Affinity and Comparative Analysis */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Content Affinity */}
          <div className="bg-white rounded-lg shadow-md p-4 transition-all duration-300 hover:shadow-lg">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Content Affinity by Segment
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Content Category
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      New Visitors
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Returning
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mobile
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Desktop
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      Homepage
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{ width: "85%" }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{ width: "62%" }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{ width: "78%" }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{ width: "71%" }}
                        ></div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      Product Pages
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{ width: "72%" }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{ width: "89%" }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{ width: "81%" }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{ width: "76%" }}
                        ></div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      Blog Articles
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{ width: "58%" }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{ width: "67%" }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{ width: "63%" }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{ width: "82%" }}
                        ></div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      Support Pages
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{ width: "42%" }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{ width: "75%" }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{ width: "59%" }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{ width: "68%" }}
                        ></div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      Checkout Flow
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{ width: "31%" }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{ width: "54%" }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{ width: "42%" }}
                        ></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{ width: "49%" }}
                        ></div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          {/* Comparative Analysis */}
          <SegmentComparision />
        </div>
      </div>
    </div>
  );
};

export default OverallStatistics;
